import React, { useRef, useEffect, useState } from "react";
import "./ThreeColumns.css";
import { useVideoManager } from "../context/VideoManager";

const ThreeColumns = ({
  mediaList,
  layout = "three-equal",
  userInteracted,
}) => {
  const mediaRefs = useRef([]);
  const [muted, setMuted] = useState({});
  const [isPlaying, setIsPlaying] = useState({});
  const [showControls, setShowControls] = useState({});
  const [volume, setVolume] = useState({});
  let hasUserInteracted = userInteracted;

  const { handleVideoUnmute } = useVideoManager();

  const handleMuteToggle = (videoRef) => {
    if (videoRef) {
      const isMuted = videoRef.muted;
      if (!isMuted) {
        handleVideoUnmute(videoRef); // Notify VideoManager
      }
      videoRef.muted = !isMuted; // Toggle mute state
    }
  };

  useEffect(() => {
    mediaRefs.current.forEach((mediaRef, index) => {
      if (mediaRef && mediaList[index].type === "video") {
        mediaRef.muted = true; // Set audio to off
        setShowControls((prev) => ({ ...prev, [index]: false }));

        // Add mouseover and mouseout event listeners
        mediaRef.addEventListener("mouseover", () => {
          setShowControls((prev) => ({ ...prev, [index]: true }));
        });

        mediaRef.addEventListener("mouseout", () => {
          setShowControls((prev) => ({ ...prev, [index]: false }));
        });

        // Add scroll event listener
        window.addEventListener("scroll", () =>
          threeColumnsHandleScrollMediaComponents(index)
        );
      }
    });

    // Add an event listener to set the flag when the user interacts with any media
    window.addEventListener("click", () => {
      hasUserInteracted = true;
    });

    return () => {
      // Remove scroll event listener on unmount
      window.removeEventListener(
        "scroll",
        threeColumnsHandleScrollMediaComponents
      );
    };
  }, [mediaList]);

  const threeColumnsHandleScrollMediaComponents = (index) => {
    const mediaElement = mediaRefs.current[index];
    if (mediaElement) {
      const mediaRect = mediaRefs.current[index].getBoundingClientRect();
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      const mediaMiddle = window.scrollY + mediaRect.top + mediaRect.height / 2;

      if (
        scrollPosition < mediaMiddle - mediaRect.height * 0.85 ||
        scrollPosition > mediaMiddle + mediaRect.height * 0.85
      ) {
        if (
          mediaRefs.current[index] !== null &&
          mediaRefs.current[index].paused !== undefined &&
          !mediaRefs.current[index].paused
        ) {
          mediaRefs.current[index].pause();
          setIsPlaying((prev) => ({ ...prev, [index]: false }));
        }
      } else {
        if (
          mediaRefs.current[index] !== null &&
          mediaRefs.current[index].paused !== undefined &&
          mediaRefs.current[index].paused
        ) {
          if (hasUserInteracted) {
            mediaRefs.current[index].play();
            setIsPlaying((prev) => ({ ...prev, [index]: true }));
          }
        }
      }
    }
  };

  const handleUnmute = (index) => {
    setMuted((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Group media by columns
  const groupMediaByColumn = () => {
    const groupedMedia = { left: [], center: [], right: [] };

    mediaList.forEach((media, index) => {
      const column = media.column || getDefaultColumn(index);
      groupedMedia[column].push({ ...media, index });
    });

    return groupedMedia;
  };

  const getDefaultColumn = (index) => {
    if (layout === "three-equal") return ["left", "center", "right"][index];
    if (layout === "first-two-spanned") return index === 0 ? "left" : "right";
    if (layout === "last-two-spanned") return index === 0 ? "left" : "right";
    return ["left", "center", "right"][index];
  };

  const renderMediaGroup = (mediaGroup) => {
    return mediaGroup.map((media) => (
      <div
        key={media.index}
        className="three-columns-media-container"
        style={{
          display: "flex",
          justifyContent: media.position || "center",
          alignItems: media.verticalAlign || "center",
          height: "100%",
          width: "100%",
        }}
      >
        {media.type === "video" && (
          <div
            className="three-columns-video-container"
            style={{
              display: "flex",
              justifyContent: media.position || "center",
              alignItems: media.verticalAlign || "center",
              height: "100%",
              width: "100%",
            }}
          >
            <video
              className="three-columns-media-video"
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              ref={(el) => (mediaRefs.current[media.index] = el)}
              src={media.src}
              loop
              muted={muted[media.index]}
              volume={volume[media.index]}
              controls={showControls[media.index]}
              onClick={(e) => handleMuteToggle(e.target)}
              onError={(e) => console.error("Video error:", e.target.error)}
              onLoadedMetadata={() => {
                if (!isPlaying[media.index]) {
                  mediaRefs.current[media.index].play();
                  setIsPlaying((prev) => ({ ...prev, [media.index]: true }));
                }
              }}
              style={{
                height: media.height || "auto",
                width: media.width || "100%",
                objectFit: media.objectFit || "contain",
              }}
            />
          </div>
        )}
        {media.type === "image" && (
          <img
            className="three-columns-media-image"
            src={media.src}
            alt={media.alt || `media-${media.index}`}
            ref={(el) => (mediaRefs.current[media.index] = el)}
            style={{
              height: media.height || "auto",
              width: media.width || "100%",
              objectFit: media.objectFit || "contain",
            }}
          />
        )}
      </div>
    ));
  };

  const groupedMedia = groupMediaByColumn();

  return (
    <div className={`grid-container-three-columns ${layout}`}>
      {layout === "three-equal" && (
        <>
          <div className="three-columns-grid-item column-1">
            {renderMediaGroup(groupedMedia.left)}
          </div>
          <div className="three-columns-grid-item column-2">
            {renderMediaGroup(groupedMedia.center)}
          </div>
          <div className="three-columns-grid-item column-3">
            {renderMediaGroup(groupedMedia.right)}
          </div>
        </>
      )}
      {layout === "first-two-spanned" && (
        <>
          <div className="three-columns-grid-item column-1">
            {renderMediaGroup(groupedMedia.left)}
          </div>
          <div className="three-columns-grid-item column-2">
            {renderMediaGroup(groupedMedia.right)}
          </div>
        </>
      )}
      {layout === "last-two-spanned" && (
        <>
          <div className="three-columns-grid-item column-1">
            {renderMediaGroup(groupedMedia.left)}
          </div>
          <div className="three-columns-grid-item column-2">
            {renderMediaGroup(groupedMedia.right)}
          </div>
        </>
      )}
    </div>
  );
};

export default ThreeColumns;
